import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {AsyncPipe} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatInputModule} from '@angular/material/input';
import {MatMenuModule} from '@angular/material/menu';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {LanguageService} from '../../../language/language.service';
import {SearchScopeConfiguration} from '../../../cache/uiconfiguration/searchscopeconfiguration';
import {CacheService} from '../../../cache/cache.service';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {FilterbuilderDialogComponent} from '../../filterbuilder/filterbuilder-dialog.component';
import {distinctUntilChanged, first, map} from 'rxjs/operators';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatFormFieldAppearance} from '@angular/material/form-field';

@Component({
  selector: 'smd-filter',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './smd-filter.component.html',
  styleUrls: ['./smd-filter.component.scss'],
  imports: [
    AsyncPipe,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatMenuModule,
    ReactiveFormsModule,
    FormsModule,
    MatTooltipModule,
  ],
})
export class SmdFilter implements OnDestroy {
  private dialog = inject(MatDialog);
  private cacheService = inject(CacheService);
  @Input() title: string;

  //region Selected Search Scope
  _selectedSearchScope: SearchScopeConfiguration;
  _selectedSearcScopeSubject: BehaviorSubject<SearchScopeConfiguration> = new BehaviorSubject(null);

  @Input() set selectedSearchScope(value: SearchScopeConfiguration) {
    this._selectedSearchScope = value;
    this._selectedSearcScopeSubject.next(value);
  }
  @Output() selectedSearchScopeChange: EventEmitter<SearchScopeConfiguration> = new EventEmitter();

  private changeSelectedSearchScope(value: SearchScopeConfiguration) {
    this._selectedSearcScopeSubject.next(value);
    this.selectedSearchScopeChange.emit(value);
  }
  //endregion

  @Input() searchScopeConfigs: SearchScopeConfiguration[] = [];

  //region Filter Variables
  @Input() filterValue: string = '';
  @Output() filterValueChange: EventEmitter<string> = new EventEmitter();
  private filterTimeout: any;
  filterDelay: number = 500;

  @Output() filterChange: EventEmitter<null> = new EventEmitter();
  //endregion

  @Input() enableFilter: boolean;
  @Input() enableFilterbuilder: boolean;

  readonly filterLabel: Observable<string>;

  @Input() appearance: MatFormFieldAppearance = 'outline';

  clearFilter(): void {
    this.filterValueChange.emit('');
    this.filterChange.emit(null);
  }

  constructor() {
    this.filterLabel = combineLatest(
      [this.cacheService.getLanguageResource(), this._selectedSearcScopeSubject],
      (lr, searchScope) =>
        searchScope?.displayname ??
        LanguageService.translate('DatatableFilterDefaultValue', 'Filter', lr),
    ).pipe(distinctUntilChanged());
  }

  readonly showScopeTooltip: Observable<string> = this.cacheService
    .getLanguageResource()
    .pipe(
      map(lr =>
        LanguageService.translate('DatatableShowScopeTooltip', 'Choose a search scope', lr),
      ),
    );

  _selectScope(sc: SearchScopeConfiguration): void {
    this.changeSelectedSearchScope(sc);
    this.resetCustomFilter();
  }

  get areMultipleSearchScopes(): boolean {
    return this.searchScopeConfigs != null && this.searchScopeConfigs.length >= 2;
  }

  private searchScopeBeforeFilterbuilder: SearchScopeConfiguration = null;
  customFilterActive: boolean = false;

  public resetCustomFilter(): void {
    this.searchScopeBeforeFilterbuilder = null;
    this.customFilterActive = false;
  }

  _filterChange(): void {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }

    this.filterTimeout = setTimeout(() => {
      this.filterChange.emit();
      this.filterTimeout = null;
    }, this.filterDelay);
  }

  _onFilterbuilderClick() {
    let dialogRef: MatDialogRef<FilterbuilderDialogComponent> = this.dialog.open(
      FilterbuilderDialogComponent,
    );
    if (this.searchScopeBeforeFilterbuilder == null) {
      this.searchScopeBeforeFilterbuilder = this._selectedSearchScope;
    }
    if (this._selectedSearchScope != null) {
      dialogRef.componentInstance.searchScope = <SearchScopeConfiguration>{
        id: 'datatable-customizedfilter',
        displayname: 'Customized Filter (Filter Builder)',
        description: 'Customized Filter (Filter Builder)',
        usagekeyword: [],
        order: 0,
        attributesearched: this._selectedSearchScope.attributesearched,
        xpath: this._selectedSearchScope.xpath,
        resultobjecttype: this._selectedSearchScope.resultobjecttype,
        attributedisplayed: this._selectedSearchScope.attributedisplayed,
      };
    }
    dialogRef.componentInstance.title = this.title;

    dialogRef
      .afterClosed()
      .pipe(first())
      .subscribe((sc: SearchScopeConfiguration | boolean) => {
        if (sc != null) {
          if (sc === false) {
            this.customFilterActive = false;
            this.changeSelectedSearchScope(this.searchScopeBeforeFilterbuilder);
            this.searchScopeBeforeFilterbuilder = null;
          } else if (sc !== true) {
            this.customFilterActive = true;
            this.changeSelectedSearchScope(sc);
          }
        }
      });
  }

  ngOnDestroy(): void {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  }
}
